import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import ApiData from "../api/ApiData";
import { observer } from "mobx-react-lite";
import Loader from "../components/Loader";
import Memory from "../api/Memory";
import RolesState from "../api/RolesState";
import User from "../api/User";
import AlertData from "../api/AlertData";
import { CcV4Toggle } from "../components/UI/CcV4Toggle";
import { InfoComponent } from "../components/prfile/InfoProfile";
import { ReqItem } from "../components/UI/ReqItem";
import { BtnOutlineV4 } from "../components/UI/button/BtnOutlineV4";
import { DashCard } from "../components/UI/DashCard";
import { HIDE_EXPIRED_SUBSCRIPTION_ALERT_KEY } from "../utils/subscriptionAlertUtils";
import NotesWidget from "../api/NotesWidget";
import LicenseCalculatorWidget from "../api/LicenseCalculatorWidget";
import { canViewLicenseCalculator } from "../utils/canViewLicenseCalculator";
import { ProfileReportTemplatesPanel } from "../components/prfile/ProfileReportTemplatesPanel";

const formatProfileName = (profile) => {
  const name = [profile?.last_name, profile?.first_name, profile?.father_name]
    .filter((part) => part != null && String(part).trim() !== "")
    .join(" ");
  return name || "—";
};

const getProfileRoleName = (profile) => {
  const fromProfile = profile?.role_rel?.role_name;
  if (fromProfile) {
    return fromProfile;
  }
  return User.user?.role_rel?.role_name || null;
};

const canPatchProfile = () =>
  RolesState.patch_my_profile &&
  (User.user?.permission?.includes("patch_my_profile") ||
    User.user?.is_superuser);

const ProfileWidgetsPanel = observer(() => {
  const showLicenseCalculatorToggle = canViewLicenseCalculator();

  return (
    <DashCard className="profile-widgets-card" title="Виджеты">
      <ul className="profile-alerts-list">
        <li className="profile-alert-row">
          <span className="profile-alert-text">Заметки</span>
          <CcV4Toggle
            active={NotesWidget.enabled}
            onClick={() => NotesWidget.setEnabled(!NotesWidget.enabled)}
          />
        </li>
        {showLicenseCalculatorToggle && RolesState.get_integration_1c_prices ? (
          <li className="profile-alert-row">
            <span className="profile-alert-text">Калькулятор лицензий</span>
            <CcV4Toggle
              active={LicenseCalculatorWidget.enabled}
              onClick={() =>
                LicenseCalculatorWidget.setEnabled(
                  !LicenseCalculatorWidget.enabled,
                )
              }
            />
          </li>
        ) : null}
      </ul>
    </DashCard>
  );
});

const ProfileAlertsPanel = observer(
  ({ alertDate, setActiveAlert, hideExpired, onHideExpiredChange }) => (
    <DashCard className="profile-alerts-card" title="Настройка уведомлений">
      <ul className="profile-alerts-list">
        {alertDate.map((a) => (
          <li key={a.name} className="profile-alert-row">
            <span className="profile-alert-text">{a.body}</span>
            <CcV4Toggle active={a.active} onClick={() => setActiveAlert(a)} />
          </li>
        ))}
        <li className="profile-alert-row">
          <span className="profile-alert-text">
            Не показывать истёкшие подписки
          </span>
          <CcV4Toggle active={hideExpired} onClick={onHideExpiredChange} />
        </li>
        <li className="profile-alert-row">
          <span className="profile-alert-text">Только мои уведомления</span>
          <CcV4Toggle
            active={AlertData.alert_show_mine_only}
            onClick={() =>
              AlertData.setAlertShowMineOnly(!AlertData.alert_show_mine_only)
            }
          />
        </li>
      </ul>
    </DashCard>
  ),
);

const UserProfile = observer(() => {
  const [edit, setEdit] = useState(false);
  const navigate = useNavigate();

  async function getUserFunc() {
    ApiData.getUser(navigate);
  }

  const [alertDate, setAlertDate] = useState([
    {
      name: "active_report_subscription",
      active: AlertData.active_report_subscription,
      nameData: "date_error_subscription",
      body: "Уведомлять, если количество дней активной подписки компании осталось 15 или менее дней",
    },
  ]);
  const [hideExpired, setHideExpired] = useState(() => {
    return localStorage.getItem(HIDE_EXPIRED_SUBSCRIPTION_ALERT_KEY) === "true";
  });

  const setActiveAlert = (e) => {
    setAlertDate((item) =>
      item.map((a) => (a.name === e.name ? { ...a, active: !a.active } : a)),
    );
    localStorage.setItem(e.name, !e.active);
    AlertData.setVisibleTagFilterCollection(e);
  };

  const onHideExpiredChange = () => {
    const next = !hideExpired;
    setHideExpired(next);
    localStorage.setItem(
      HIDE_EXPIRED_SUBSCRIPTION_ALERT_KEY,
      next ? "true" : "false",
    );
    AlertData.syncHideExpiredFromStorage();
  };

  useEffect(() => {
    Memory.funcSelectDayPeriodFlag(false);
    getUserFunc();
  }, []);

  const logOut = () => {
    ApiData.logout();
    navigate("/login");
  };

  if (!RolesState.get_my_profile) {
    return <Navigate to="/noright" replace />;
  }

  const profile = ApiData.user_profile;
  const roleName = profile ? getProfileRoleName(profile) : null;

  return (
    <div className="main-page company-card-v4">
      {profile ? (
        <>
          <div className="profile-page-layout">
            <div className="profile-page-col-personal">
              {edit ? (
                <InfoComponent edit={edit} setEdit={setEdit} />
              ) : (
                <header className="card header-card profile-personal-card">
                  <div className="header-top">
                    <div className="company-title">
                      <div className="compamy-title-name-container">
                        <span className="req-label">Личная информация</span>
                        <h1>{formatProfileName(profile)}</h1>
                      </div>
                    </div>
                    {canPatchProfile() ? (
                      <BtnOutlineV4
                        onClick={() => setEdit(true)}
                        text="Редактировать"
                      />
                    ) : null}
                  </div>

                  <div className="requisites-grid">
                    <ReqItem
                      label="Телефон"
                      value={profile.phone || "—"}
                      mono
                    />
                    <ReqItem label="Email" value={profile.email || "—"} mono />
                    <ReqItem
                      label="Роль"
                      value={roleName || "—"}
                      muted={!roleName}
                    />
                  </div>
                </header>
              )}
            </div>

            <div className="profile-page-col-alerts">
              <ProfileAlertsPanel
                alertDate={alertDate}
                setActiveAlert={setActiveAlert}
                hideExpired={hideExpired}
                onHideExpiredChange={onHideExpiredChange}
              />
              <ProfileWidgetsPanel />
              <ProfileReportTemplatesPanel />
            </div>
          </div>

          <footer className="profile-logout-footer">
            <BtnOutlineV4 onClick={logOut} text="Выход" />
          </footer>
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
});

export { UserProfile };
